/**
 * ProseMirror plugin that maintains an incremental registry of zyVersion nodes in the document.
 *
 * Tracks which version tokens appear (e.g. "java", "cpp") and whether any zyVersion nodes exist,
 * so UI code can read `getZyVersions()` / `hasZyVersionNodes()` without scanning the full doc on
 * every update. Updates incrementally by mirroring ReplaceStep / ReplaceAroundStep changes; falls
 * back to a full doc scan when the transaction is a whole-document replace or contains steps this
 * plugin cannot mirror.
 */
import type { Fragment, Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { ReplaceAroundStep, ReplaceStep } from '@tiptap/pm/transform';

const ZY_VERSION_NODE_TYPES = new Set(['zyVersionBlock', 'zyVersionInline']);

/** Internal plugin state: reference counts per token plus total zyVersion node count. */
export type ZyVersionRegistryPluginState = {
    versionCounts: Map<string, number>;
    nodeCount: number;
};

/** Public-facing snapshot derived from internal state for UI and utilities. */
export type ZyVersionRegistryState = {
    versions: string[];
    hasVersionNodes: boolean;
};

export const zyVersionRegistryPluginKey = new PluginKey<ZyVersionRegistryPluginState>('zyVersionRegistry');

export const EMPTY_ZY_VERSION_REGISTRY: ZyVersionRegistryState = {
    versions: [],
    hasVersionNodes: false,
};

function isZyVersionNode(node: PMNode): boolean {
    return ZY_VERSION_NODE_TYPES.has(node.type.name);
}

/**
 * Parses a comma-separated version attribute into unique, trimmed tokens.
 * Empty or whitespace-only strings yield an empty list.
 */
export function extractVersionTokens(version: string): string[] {
    if (!version.trim()) {
        return [];
    }
    return [
        ...new Set(
            version
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
        ),
    ];
}

function createEmptyInternal(): ZyVersionRegistryPluginState {
    return { versionCounts: new Map(), nodeCount: 0 };
}

function cloneInternal(internal: ZyVersionRegistryPluginState): ZyVersionRegistryPluginState {
    return {
        nodeCount: internal.nodeCount,
        versionCounts: new Map(internal.versionCounts),
    };
}

/** Converts internal reference counts into a sorted token list and a has-nodes flag. */
export function toPublicRegistryState(internal: ZyVersionRegistryPluginState): ZyVersionRegistryState {
    const versions = Array.from(internal.versionCounts.entries())
        .filter(([, count]) => count > 0)
        .map(([token]) => token)
        .sort();
    return {
        versions,
        hasVersionNodes: internal.nodeCount > 0,
    };
}

function registerNode(internal: ZyVersionRegistryPluginState, node: PMNode): void {
    if (!isZyVersionNode(node)) {
        return;
    }
    internal.nodeCount += 1;
    for (const token of extractVersionTokens(String(node.attrs.version ?? ''))) {
        internal.versionCounts.set(token, (internal.versionCounts.get(token) ?? 0) + 1);
    }
}

function unregisterNode(internal: ZyVersionRegistryPluginState, node: PMNode): void {
    if (!isZyVersionNode(node)) {
        return;
    }
    internal.nodeCount = Math.max(0, internal.nodeCount - 1);
    for (const token of extractVersionTokens(String(node.attrs.version ?? ''))) {
        const next = (internal.versionCounts.get(token) ?? 0) - 1;
        if (next <= 0) {
            internal.versionCounts.delete(token);
        } else {
            internal.versionCounts.set(token, next);
        }
    }
}

/** Returns a zyVersion node that exactly spans [from, to), e.g. the target of setNodeMarkup on a block wrapper. */
function getSpanningZyVersionAtRange(doc: PMNode, from: number, to: number): PMNode | null {
    if (from >= to) {
        return null;
    }
    const node = doc.resolve(from).nodeAfter;
    if (!node || !isZyVersionNode(node)) {
        return null;
    }
    if (from + node.nodeSize === to) {
        return node;
    }
    return null;
}

/** Unregisters zyVersion nodes fully contained in [from, to), not merely overlapping. */
function unregisterFullyContainedRange(doc: PMNode, from: number, to: number, internal: ZyVersionRegistryPluginState): void {
    if (from >= to) {
        return;
    }
    doc.nodesBetween(from, to, (node, pos) => {
        if (!isZyVersionNode(node)) {
            return;
        }
        const nodeFrom = pos;
        const nodeTo = pos + node.nodeSize;
        if (nodeFrom >= from && nodeTo <= to) {
            unregisterNode(internal, node);
        }
    });
}

function visitSubtree(node: PMNode, visitor: (node: PMNode) => void): void {
    visitor(node);
    node.descendants(descendant => {
        visitor(descendant);
    });
}

/** Registers every zyVersion node in a slice fragment, including nested ones. */
function registerFragment(fragment: Fragment, internal: ZyVersionRegistryPluginState): void {
    fragment.forEach(node => {
        visitSubtree(node, descendant => {
            if (isZyVersionNode(descendant)) {
                registerNode(internal, descendant);
            }
        });
    });
}

/** Builds registry state by scanning the entire document (used on init and as fallback). */
export function buildRegistryFromDoc(doc: PMNode): ZyVersionRegistryPluginState {
    const internal = createEmptyInternal();
    doc.descendants(node => {
        if (isZyVersionNode(node)) {
            registerNode(internal, node);
        }
    });
    return internal;
}

/**
 * Detects a single ReplaceStep that replaces the entire document content.
 * Common for setContent / setContentWithMeta and is cheaper to handle via full rescan.
 */
export function isFullDocumentReplace(tr: Transaction, oldDoc: PMNode): boolean {
    if (tr.steps.length !== 1) {
        return false;
    }
    const step = tr.steps[0];
    if (!(step instanceof ReplaceStep)) {
        return false;
    }
    return step.from === 0 && step.to === oldDoc.content.size;
}

/**
 * Mirrors a ReplaceStep on the registry: subtract removed nodes, add inserted slice content,
 * then advance a shadow doc so subsequent steps in the same transaction see correct positions.
 *
 * Only fully contained zyVersion nodes are unregistered. Merely intersecting parents (e.g. a
 * zyVersionBlock when UniqueID rewrites an inner paragraph) must stay registered — they are not
 * present in `step.slice` when the slice's open depth keeps the wrapper outside the replacement.
 */
function applyReplaceStep(step: ReplaceStep, doc: PMNode, internal: ZyVersionRegistryPluginState): PMNode | null {
    unregisterFullyContainedRange(doc, step.from, step.to, internal);
    registerFragment(step.slice.content, internal);
    const result = step.apply(doc);
    if (result.failed || !result.doc) {
        return null;
    }
    return result.doc;
}

/**
 * Mirrors a ReplaceAroundStep: the gap between gapFrom and gapTo is preserved, so only the
 * outer ranges [from, gapFrom) and [gapTo, to) are unregistered before registering new content.
 * Wrapper nodes that exactly span [from, to) are swapped after apply (e.g. setNodeMarkup attr changes).
 * Overlapping-but-not-contained nodes are left registered because they survive in the gap.
 */
function applyReplaceAroundStep(step: ReplaceAroundStep, doc: PMNode, internal: ZyVersionRegistryPluginState): PMNode | null {
    const oldSpanningNode = getSpanningZyVersionAtRange(doc, step.from, step.to);
    unregisterFullyContainedRange(doc, step.from, step.gapFrom, internal);
    unregisterFullyContainedRange(doc, step.gapTo, step.to, internal);
    registerFragment(step.slice.content, internal);
    const result = step.apply(doc);
    if (result.failed || !result.doc) {
        return null;
    }
    if (oldSpanningNode) {
        unregisterNode(internal, oldSpanningNode);
    }
    const newSpanningNode = getSpanningZyVersionAtRange(result.doc, step.from, step.to);
    if (newSpanningNode) {
        registerNode(internal, newSpanningNode);
    }
    return result.doc;
}

/**
 * Updates registry state for a document-changing transaction.
 * Uses incremental step mirroring when possible; otherwise rescans newDoc.
 */
export function applyRegistryTransaction(
    oldInternal: ZyVersionRegistryPluginState,
    tr: Transaction,
    oldDoc: PMNode,
    newDoc: PMNode,
): ZyVersionRegistryPluginState {
    if (isFullDocumentReplace(tr, oldDoc)) {
        return buildRegistryFromDoc(newDoc);
    }

    const internal = cloneInternal(oldInternal);
    let doc = oldDoc;

    for (const step of tr.steps) {
        let nextDoc: PMNode | null;
        if (step instanceof ReplaceStep) {
            nextDoc = applyReplaceStep(step, doc, internal);
        } else if (step instanceof ReplaceAroundStep) {
            nextDoc = applyReplaceAroundStep(step, doc, internal);
        } else {
            return buildRegistryFromDoc(newDoc);
        }
        if (!nextDoc) {
            return buildRegistryFromDoc(newDoc);
        }
        doc = nextDoc;
    }

    return internal;
}

/** Creates the plugin that keeps the zyVersion registry in sync with editor transactions. */
export function createZyVersionRegistryPlugin(): Plugin {
    return new Plugin({
        key: zyVersionRegistryPluginKey,
        state: {
            init(_, state: EditorState) {
                return buildRegistryFromDoc(state.doc);
            },
            apply(tr, oldInternal, oldEditorState, newEditorState) {
                if (!tr.docChanged) {
                    return oldInternal;
                }
                return applyRegistryTransaction(oldInternal, tr, oldEditorState.doc, newEditorState.doc);
            },
        },
    });
}
